import { useCallback, useEffect } from 'react';

import {
  useAtom,
  useAtomValue,
  useSetAtom,
} from 'jotai';
import { useNavigate } from 'react-router-dom';
import { useLocalStorage } from 'usehooks-ts';

import {
  buildRegisterRequest,
  cleanUpRegistrationData,
  cookieJar,
  uiRoutes,
  useRegisterV2,
} from '@consumer-frontend/nc-api';
import {
  emptyAddress,
  RegistrationAddressT,
  somethingWentWrongAlertBannerProps,
  STATE_LOCAL_STORAGE_KEY,
  StateValueT,
} from '@consumer-frontend/nc-utils';
import { Container } from '@consumer-frontend/shared-ui';

import { useApplyRouting } from '../../routing';
import {
  accountIdAtom,
  alertBannerAtom,
  hasVerifiedIdentityAtom,
  registrationDataAtom,
  unableToContinueReasonAtom,
} from '../../store';
import { NcAddressForm } from '../NcAddressForm';
import { NcPageWithAlertBanner } from '../NcPageWithAlertBanner';
import { NcProgressBar } from '../NcProgressBar';

import '@consumer-bootstrap/netcredit.scss';
// eslint-disable-next-line import/order
import '@consumer-bootstrap/brands/netcredit/apply/address-page.scss';

export const NcAddressPage = () => {
  const [registrationData, setRegistrationData] = useAtom(registrationDataAtom);
  const [_, setAddressState] = useLocalStorage<StateValueT | ''>(STATE_LOCAL_STORAGE_KEY, '');
  const {
    handleBack,
    handleContinue,
    progress,
  } = useApplyRouting();
  const hasVerifiedIdentity = useAtomValue(hasVerifiedIdentityAtom);
  const refreshAccountId = useSetAtom(accountIdAtom);
  const setAlertBanner = useSetAtom(alertBannerAtom);
  const setUnableToContinueReason = useSetAtom(unableToContinueReasonAtom);
  const navigate = useNavigate();

  const {
    error,
    registerV2: registerAccount,
  } = useRegisterV2();

  // show alert banner on error & navigate previously verified customers to unable-to-continue on 409's
  useEffect(() => {
    if (error) {
      if (hasVerifiedIdentity && error.response?.status === 409) {
        setUnableToContinueReason('unableToVerify');
        navigate(uiRoutes.apply.unableToContinue);

        return;
      }

      setAlertBanner(somethingWentWrongAlertBannerProps);
    }
  }, [
    error,
    hasVerifiedIdentity,
    navigate,
    setAlertBanner,
    setUnableToContinueReason,
  ]);

  const handleSubmit = useCallback(
    async (address: RegistrationAddressT): Promise<void> => {
      setAddressState(address.state);

      // SSN verified customers from non-WI states submit their data at this point
      if (hasVerifiedIdentity && address.state !== 'WI') {
        const updatedRegistrationData = { ...registrationData, address };
        const requestData = buildRegisterRequest({
          registrationData: updatedRegistrationData,
        });

        const response = await registerAccount(requestData);

        if (!response) {
          return;
        }

        cookieJar.saveTokens(response.accessToken, response.refreshToken);
        refreshAccountId();

        await handleContinue({ address });

        setRegistrationData(cleanUpRegistrationData(updatedRegistrationData));
      } else {
        await handleContinue({ address });
      }
    },
    [
      handleContinue,
      hasVerifiedIdentity,
      refreshAccountId,
      registerAccount,
      registrationData,
      setAddressState,
      setRegistrationData,
    ],
  );

  return (
    <NcPageWithAlertBanner>
      <Container className="nc-address-page" hasBoxShadow>
        <NcProgressBar progress={progress} />

        <h1 className="h3">Address</h1>

        <NcAddressForm
          defaultValues={registrationData.address ?? emptyAddress}
          onBack={handleBack}
          onSubmit={handleSubmit}
        />
      </Container>
    </NcPageWithAlertBanner>
  );
};
